import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

const key = import.meta.env.VITE_POSTHOG_KEY;
const host = import.meta.env.VITE_POSTHOG_HOST;

// Known-harmless exception messages to drop before they're sent — browser
// noise, extensions, cancelled requests. Mirrors the IGNORED_ERRORS list in
// `lib/sentry.ts`; keep the two in sync.
const IGNORED_ERRORS = [
  // Browser/network noise
  'ResizeObserver loop',
  'Non-Error promise rejection captured',
  'Load failed',
  'Failed to fetch',
  'NetworkError',
  'AbortError',
  'signal is aborted without reason',
  'TypeError: cancelled',
  'TypeError: Cancelled',
  // Browser extensions
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  // Common harmless errors
  'Script error.',
  'ChunkLoadError',
];

// Drop $exception events whose type/message matches the ignore list, so known
// browser noise never reaches PostHog Error Tracking (saves quota + keeps the
// issue list readable). Returns null to discard, the event to keep.
function filterIgnoredExceptions(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== '$exception') return event;
  const exceptions = event.properties?.$exception_list ?? [];
  const haystack = exceptions
    .map((e: { type?: string; value?: string }) => `${e?.type ?? ''}: ${e?.value ?? ''}`)
    .join('\n');
  if (IGNORED_ERRORS.some((needle) => haystack.includes(needle))) return null;
  return event;
}

if (key && host) {
  posthog.init(key, {
    api_host: host,
    // Capture pageviews automatically; we'll add custom events from app code.
    capture_pageview: true,
    capture_pageleave: true,
    // Capture uncaught browser errors + unhandled promise rejections, with
    // stack traces, into PostHog Error Tracking.
    capture_exceptions: true,
    // Drop known-harmless browser noise before it's sent (see IGNORED_ERRORS).
    before_send: filterIgnoredExceptions,
    // Privacy: mask form inputs in session replays by default. Loosen
    // per-element with `data-ph-no-capture` / `ph-no-capture` classes
    // if a specific surface needs the unmasked value.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive]',
    },
    // Don't ping the API in dev unless we explicitly want to.
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug();
    },
  });
}

export default posthog;
